import { useCallback, useMemo } from "react";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";

/**
 * Single-character label for a material (timeline markers, video short labels).
 * Prefers catalog `short_name` when set, otherwise the first letter A–Z in the
 * legal name (skips leading punctuation like "#"), then first digit, then first char.
 */
export const getMaterialSymbol = (
  materialName: string,
  catalogShortByName?: Record<string, string>
): string => {
  const fromCatalog = catalogShortByName?.[materialName]?.trim();
  const source =
    fromCatalog && fromCatalog.length > 0 ? fromCatalog : materialName;
  const letter = /[A-Za-z]/.exec(source);
  if (letter) {
    return letter[0].toUpperCase();
  }
  const digit = /[0-9]/.exec(source);
  if (digit) {
    return digit[0];
  }
  return source.toUpperCase().charAt(0) || "?";
};

/**
 * Material symbol resolver for the strategy heats dashboard (requires {@link HeatsDataProvider}).
 */
export const useMaterialSymbol = (): {
  getMaterialSymbol: (materialName: string) => string;
} => {
  const { materials } = useHeatsData();
  const catalogShortByName = useMemo(
    () =>
      materials
        ? Object.fromEntries(materials.map((m) => [m.name, m.short_name]))
        : undefined,
    [materials]
  );

  const resolve = useCallback(
    (materialName: string) =>
      getMaterialSymbol(materialName, catalogShortByName),
    [catalogShortByName]
  );

  return { getMaterialSymbol: resolve };
};
